import React, { useEffect, useRef, useState } from 'react';
import { useSettings } from '../context/SettingsContext';

/**
 * InstallPrompt — «همیشه» از کاربر دعوت به نصب PWA می‌کند:
 *
 *  - موبایل: بنر شناور پایین صفحه (تا نصب شود یا بسته شود دوباره نمایش
 *    داده می‌شود — طبق تصمیم محصول، محدودیت تکرار نداریم).
 *  - دسکتاپ: دکمهٔ نصب در گوشهٔ پایین صفحه.
 *  - از رویداد `beforeinstallprompt` بریفرهای استفاده می‌کند تا با کلیک،
 *    نصب واقعی انجام شود؛ در مرورگرهایی که این رویداد را نمی‌دهند
 *    (مثلاً Safari iOS) راهنمای دستی «افزودن به صفحهٔ اصلی» نمایش داده می‌شود.
 *
 * قفل‌های نمایش:
 *  - اگر `navigator.standalone` (اپ نصب‌شده) باشد → اصلا نمایش داده نمی‌شود.
 *  - فقط در production (hostname غیر-محل)؛ در dev/localhost خاموش است.
 */

const isLocalHost = () => {
  try {
    const h = window.location.hostname || '';
    return (
      h === 'localhost' ||
      h === '127.0.0.1' ||
      h === '0.0.0.0' ||
      h.startsWith('10.') ||
      h.startsWith('192.168.') ||
      /^172\.(1[6-9]|2\d|3[0-1])\./.test(h)
    );
  } catch (_) {
    return true;
  }
};

const isStandalone = () => {
  try {
    if (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) return true;
    return window.navigator.standalone === true;
  } catch (_) {
    return false;
  }
};

/** Returns true when the PWA was installed from this browser previously. */
const isInstalled = () => {
  try {
    return localStorage.getItem('kadolar_pwa_installed') === '1';
  } catch (_) {
    return false;
  }
};

const DownloadIcon = () => (
  <svg className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M12 4v12m0 0l-4-4m4 4l4-4" />
  </svg>
);

export default function InstallPrompt() {
  const { systemSettings, resolveMediaUrl } = useSettings();
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [enabled, setEnabled] = useState(false);
  const [installed, setInstalled] = useState(false);
  const installStartedRef = useRef(false);

  // تشخیص نصب بودن اپ — مرورگرهای دسکتاپ روش مستقیمی ندارند؛
  // ترکیبی از localStorage (set هنگام appinstalled) + beforeinstallprompt
  // (وجودش یعنی هنوز نصب نیست) استفاده می‌شود.
  useEffect(() => {
    try {
      if (localStorage.getItem('kadolar_pwa_installed') === '1') {
        setInstalled(true);
      }
    } catch (_) {}
  }, []);

  useEffect(() => {
    // نمایش در production + غیر-standalone. در دسکتاپ همیشه (با دکمهٔ بستن)
    // و در موبایل با بنر پایین. برای dev/localhost هم فعال می‌شود تا
    // تست‌پذیر باشد.
    setEnabled(!isStandalone());
  }, []);

  // تشخیص موبایل (برای انتخاب بنر vs دکمه)
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 768px)');
    const update = () => setIsMobile(mq.matches);
    update();
    mq.addEventListener?.('change', update);
    return () => mq.removeEventListener?.('change', update);
  }, []);

  // ── مخفی‌کردن بنر وقتی کاربر به انتهای صفحه رسیده ─────────────────────
  // بنر «شناور» است و روی فوتر (لینک‌های «قوانین»/«حریم خصوصی» و نشان
  // اِنماد) و نوار ناوبری پایین می‌پوشید. برای اینکه همهٔ لینک‌های پایین
  // صفحه همیشه قابل دسترس باشند، به‌محض رسیدن به انتهای صفحه (یا کوتاه
  // بودن صفحه به‌قدری که اصلاً اسکرول نشود) بنر مخفی می‌شود و با بالا
  // رفتن دوباره نمایش داده می‌شود.
  const [atBottom, setAtBottom] = useState(false);
  useEffect(() => {
    if (!enabled) return undefined;
    const update = () => {
      const doc = document.documentElement;
      const max = Math.max(
        doc.scrollHeight || 0,
        (document.body && document.body.scrollHeight) || 0
      );
      const y = window.scrollY || window.pageYOffset || 0;
      setAtBottom(max - (y + window.innerHeight) < 160);
    };
    update();
    window.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    // محتوا بعد از mount هم بارگذاری می‌شود (دیتا/تصاویر)؛ ارتفاع سند را هم
    // رصد می‌کنیم تا وضعیت «انتهای صفحه» دقیق بماند.
    let ro = null;
    try {
      const target = document.body || document.documentElement;
      if (target && typeof ResizeObserver !== 'undefined') {
        ro = new ResizeObserver(update);
        ro.observe(target);
      }
    } catch (_) {}
    return () => {
      window.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
      if (ro) { try { ro.disconnect(); } catch (_) {} }
    };
  }, [enabled]);

  // دریافت رویداد beforeinstallprompt (Chrome/Edge/Android)
  useEffect(() => {
    if (!enabled) return;
    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      // قبل از نصب، flag نصب را پاک کنیم (احتمال uninstall + reinstall).
      try { localStorage.removeItem('kadolar_pwa_installed'); } catch (_) {}
      setInstalled(false);
    };
    const onInstalled = () => {
      setDeferredPrompt(null);
      setDismissed(false);
      setShowHelp(false);
      try { localStorage.setItem('kadolar_pwa_installed', '1'); } catch (_) {}
      setInstalled(true);
    };
    window.addEventListener('beforeinstallprompt', onBeforeInstall);
    window.addEventListener('appinstalled', onInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstall);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, [enabled]);

  // ── Re-check on every tab return (visibility) ─────────────────────────
  // مرورگرها API عمومی «آیا این PWA در این دستگاه نصب است» ندارند؛ قوی‌ترین
  // سیگنال‌ها: (1) display-mode:standalone = پنجرهٔ خودِ اپ نصب‌شده باز است،
  // (2) flag دائمی kadolar_pwa_installed (set هنگام appinstalled). هر بار که
  // کاربر به تب برمی‌گردد (یا صفحه بارگذاری می‌شود) دوباره چک می‌کنیم؛
  // اگر نصب باشد، بنر نمایش داده نمی‌شود.
  useEffect(() => {
    const recheck = () => {
      if (isStandalone()) {
        // داخل خودِ اپ نصب‌شده هستیم — قطعاً نصب است.
        try { localStorage.setItem('kadolar_pwa_installed', '1'); } catch (_) {}
        setInstalled(true);
        return;
      }
      // غیر-standalone: فقط flag برقرار است را قبول می‌کنیم.
      try {
        if (localStorage.getItem('kadolar_pwa_installed') === '1') setInstalled(true);
      } catch (_) {}
    };
    recheck(); // موقع mount
    document.addEventListener('visibilitychange', recheck);
    window.addEventListener('focus', recheck);
    return () => {
      document.removeEventListener('visibilitychange', recheck);
      window.removeEventListener('focus', recheck);
    };
  }, []);

  if (!enabled) return null;
  if (isStandalone()) return null;
  if (installed) return null;

  const doInstall = async () => {
    if (installStartedRef.current) return;
    installStartedRef.current = true;
    try {
      if (deferredPrompt) {
        deferredPrompt.prompt();
        const choice = await deferredPrompt.userChoice;
        if (choice?.outcome === 'accepted') {
          // نصب پذیرفته شد — flag دائمی را همین حالا بگذاریم تا
          // در رفرش‌های بعد بنر نمایش داده نشود (روی دسکتاپ Chrome
          // رویداد appinstalled همیشه نمی‌آید).
          try { localStorage.setItem('kadolar_pwa_installed', '1'); } catch (_) {}
          setInstalled(true);
          setDismissed(true);
        } else {
          // رد شد؛ بنر را باز نگه‌داریم تا دوباره تلاش کند.
          installStartedRef.current = false;
        }
        setDeferredPrompt(null);
        installStartedRef.current = false;
        return;
      }
      // مرورگری که beforeinstallprompt نمی‌دهد (مثلاً iOS): راهنمای دستی
      setShowHelp(true);
    } catch (_) {
      installStartedRef.current = false;
    }
  };

  // ── تشخیص مرورگر برای راهنمای دقیقِ دستی ───────────────────────────────
  const detectBrowser = () => {
    try {
      const ua = navigator.userAgent || '';
      if (/Edg\//.test(ua)) return 'edge';
      if (/Chrome\//.test(ua)) return 'chrome';
      if (/Safari\//.test(ua) && /Version\//.test(ua)) return 'safari';
      if (/Firefox\//.test(ua)) return 'firefox';
      return 'unknown';
    } catch (_) {
      return 'unknown';
    }
  };

  const manualGuide = (() => {
    const browser = detectBrowser();
    if (isMobile) {
      if (browser === 'safari') {
        return [
          'آیکون اشتراک‌گذاری (⬆) در نوار پایین Safari را بزنید.',
          'گزینهٔ «Add to Home Screen» / «افزودن به صفحهٔ اصلی» را انتخاب کنید.',
          'تایید کنید — آیکون کادولار به صفحهٔ اصلی گوشی اضافه می‌شود.',
        ];
      }
      // Android (Chrome)
      return [
        'آیکون منو (⋮) بالای صفحه را بزنید.',
        'گزینهٔ «Install app» / «نصب اپلیکیشن» را انتخاب کنید.',
        'تایید کنید — آیکون کادولار به صفحهٔ اصلی گوشی اضافه می‌شود.',
      ];
    }
    // Desktop
    if (browser === 'edge') {
      return [
        'آیکون «نصب» (⊕ با فلش) در نوار آدرس را بزنید، یا',
        'منو (⋯) ← «Apps» ← «Install Kadolar» / «کادولار را نصب کنید».',
        'در ویندوز: آیکون به Start Menu و دسکتاپ اضافه می‌شود.',
      ];
    }
    if (browser === 'chrome') {
      return [
        'آیکون «نصب» (⊕ / دانلود با فلش) در انتهای نوار آدرس را بزنید، یا',
        'منو (⋮) ← «Cast, save, and share» / «نمایش، ذخیره و اشتراک‌گذاری» ← «Install page as app…».',
        'در ویندوز: آیکون به Start Menu و دسکتاپ اضافه می‌شود.',
      ];
    }
    // Firefox / others — no desktop PWA support yet
    return [
      'متاسفانه این مرورگر هنوز نصب PWA روی دسکتاپ را پشتیبانی نمی‌کند.',
      'از Chrome یا Edge استفاده کنید تا اپ کادولار را به‌صورت دسکتاپی نصب کنید.',
    ];
  })();

  const iconSrc = (() => {
    try {
      const pwaIcon = systemSettings?.pwa_icon_url ? resolveMediaUrl(systemSettings.pwa_icon_url) : '';
      if (pwaIcon) return pwaIcon;
      const favicon = systemSettings?.favicon_url ? resolveMediaUrl(systemSettings.favicon_url) : '';
      if (favicon) return favicon;
      const logo = systemSettings?.logo_url ? resolveMediaUrl(systemSettings.logo_url) : '';
      if (logo) return logo;
    } catch (_) {}
    return '/logo192.png';
  })();

  // ── UI ─────────────────────────────────────────────────────────────────
  if (isMobile) {
    if (dismissed || showHelp === 'dismissed-help' || isInstalled() || atBottom) return null;
    return (
      <div
        role="dialog"
        aria-label="نصب کادولار"
        className="fixed inset-x-0 z-[1050] p-3 md:p-4 bottom-[calc(72px+env(safe-area-inset-bottom))] lg:bottom-0"
      >
        <div className="mx-auto max-w-xl bg-white rounded-2xl shadow-2xl border border-kadolar-200 p-4 flex items-center gap-3">
          <img src={iconSrc} alt="کادولار" className="h-12 w-12 rounded-xl object-cover bg-gray-100" />
          <div className="flex-1 min-w-0">
            <p className="font-bold text-gray-900 text-sm">کادولار را نصب کنید</p>
            <p className="text-xs text-gray-500 mt-1">
              دسترسی سریع، بدون بارگذاری اضافی — مثل یک اپلیکیشن واقعی.
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {deferredPrompt ? (
              <button
                type="button"
                onClick={doInstall}
                className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-xl bg-kadolar-600 text-white text-sm font-bold hover:bg-kadolar-700"
              >
                <DownloadIcon />
                نصب
              </button>
            ) : (
              <button
                type="button"
                onClick={() => setShowHelp(true)}
                className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-xl bg-kadolar-600 text-white text-sm font-bold hover:bg-kadolar-700"
              >
                <DownloadIcon />
                نصب
              </button>
            )}
            <button
              type="button"
              onClick={() => setDismissed(true)}
              aria-label="بستن"
              className="h-11 w-11 md:h-9 md:w-9 rounded-full text-gray-400 hover:text-gray-700 hover:bg-gray-100"
            >
              ×
            </button>
          </div>
        </div>
        {showHelp && (
          <div className="mx-auto mt-2 max-w-xl bg-kadolar-50 border border-kadolar-200 rounded-2xl p-4 text-sm text-kadolar-900">
            <p className="font-bold mb-2">نصب روی گوشی (i‌OS / اندروید)</p>
            <ol className="list-decimal pr-5 space-y-1.5 text-xs leading-6">
              {manualGuide.map((step, i) => <li key={i}>{step}</li>)}
            </ol>
            <button
              type="button"
              onClick={() => setDismissed(true)}
              className="mt-3 text-xs font-bold text-kadolar-700 underline"
            >
              متوجه شدم
            </button>
          </div>
        )}
      </div>
    );
  }

  // Desktop: floating install banner (bottom-left, RTL-safe) — always shown
  // until the user dismisses it; matches the mobile UX (no repeat limit).
  // Hidden when the PWA is already installed in this browser.
  if (dismissed || isInstalled() || atBottom) return null;
  // ── If beforeinstallprompt is available, the "نصب" button triggers the
  //    native install dialog directly — no need for the text guide. ──
  const bannerLabel = deferredPrompt ? 'نصب' : 'راهنمای نصب';
  if (showHelp) {
    return (
      <div
        role="dialog"
        aria-label="راهنمای نصب کادولار"
        className="fixed left-4 bottom-[calc(72px+env(safe-area-inset-bottom))] lg:bottom-4 z-[1050] w-[380px] max-w-[calc(100vw-2rem)] bg-white rounded-2xl shadow-2xl border border-kadolar-200 p-4"
      >
        <p className="font-bold text-gray-900 text-sm mb-2">نصب کادولار روی دسکتاپ</p>
        <ol className="list-decimal pr-5 space-y-1.5 text-xs text-gray-600 leading-6">
          {manualGuide.map((step, i) => <li key={i}>{step}</li>)}
        </ol>
        <button
          type="button"
          onClick={() => { setDismissed(true); setShowHelp(false); }}
          className="mt-3 text-xs font-bold text-kadolar-700 underline"
        >
          متوجه شدم
        </button>
      </div>
    );
  }
  return (
    <div
      role="dialog"
      aria-label="نصب کادولار"
      className="fixed left-4 bottom-[calc(72px+env(safe-area-inset-bottom))] lg:bottom-4 z-[1050] w-[340px] max-w-[calc(100vw-2rem)]"
    >
      <div className="bg-white rounded-2xl shadow-2xl border border-kadolar-200 p-4 flex items-center gap-3">
        <img src={iconSrc} alt="کادولار" className="h-14 w-14 rounded-xl object-cover bg-gray-100 shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="font-bold text-gray-900 text-sm">کادولار را نصب کنید</p>
          <p className="text-xs text-gray-500 mt-1">
            دسترسی سریع، بدون بارگذاری اضافی — مثل یک اپلیکیشن واقعی.
          </p>
          <button
            type="button"
            onClick={doInstall}
            className="mt-2 inline-flex items-center gap-1.5 px-4 py-2.5 rounded-xl bg-kadolar-600 text-white text-sm font-bold hover:bg-kadolar-700"
          >
            <DownloadIcon />
            {bannerLabel}
          </button>
        </div>
        <button
          type="button"
          onClick={() => setDismissed(true)}
          aria-label="بستن"
          className="h-11 w-11 md:h-9 md:w-9 rounded-full text-gray-400 hover:text-gray-700 hover:bg-gray-100 shrink-0"
        >
          ×
        </button>
      </div>
    </div>
  );
}
